import SiteNav from "./components/SiteNav";
import SiteFooter from "./components/ReusableComponents/Footer";
import DxrHeroCarousel from "./components/DxrHeroCarousel";
import DxrOnboarding from "./components/DxrOnboarding";
import DxrCapabilities from "./components/DxrCapabilities";
import DxrDictateLess from "./components/DxrDictateLess";
import DxrResults from "./components/DxrResults";
import FaqAccordion from "./components/FaqAccordion";
import BookDemoButton from "./components/BookDemoButton";
import FullpageScroll from "./components/FullpageScroll";
import { useEffect } from "react";
import { loadClarity } from "./clarity";

// Marketing landing for dxr.natoe.ai (the "/" route). "Try now" opens the real product workspace in
// no-login guest mode (/dashboard?trial=1); it falls back to /signup when the backend trial is off.
const TRY_URL = "/dashboard?trial=1";

const dxrFaqs = [
  {
    question: "What exactly does DXR do?",
    answer:
      "DXR is an AI radiology reporting tool. You dictate a case naturally, and DXR generates a structured, formatted report — organized into sections like Technique, Findings, and Impression — in seconds. You review, edit, and sign off.",
  },
  {
    question: "Does DXR replace the radiologist?",
    answer:
      "No. DXR handles the mechanical work — transcription, structuring, and formatting. Every report is reviewed and signed off by you. You remain fully in control of the clinical content.",
  },
  {
    question: "Can I use my own report templates?",
    answer:
      "Yes. Upload the templates you already use — one at a time or as a bulk import — then edit and categorize them by modality. DXR reads your dictation, recognizes the study, and applies the matching template automatically — or, with no template, structures your free-form dictation into a clean, sectioned report on its own.",
  },
  {
    question: "Is it secure?",
    answer:
      "DXR authenticates with secure, HttpOnly session cookies over HTTPS, so your session credential is kept out of client-side scripts. You can sign in with Google or email and sign out of your session at any time. It's built for the data-handling expectations of clinical environments.",
  },
];

const btn = {
  fontFamily: "Inter, sans-serif",
  fontSize: "17px",
  fontWeight: 600,
  padding: "15px 36px",
  borderRadius: "999px",
  textDecoration: "none",
};

export default function Landing() {
  // Marketing-only analytics. Loads Microsoft Clarity for anonymous visitors on this public page;
  // never runs in the authenticated app (see ./clarity). No-op until REACT_APP_CLARITY_PROJECT_ID is set.
  useEffect(() => { loadClarity(); }, []);

  return (
    <div className="dxr-landing">
      <style>{`
        .dxr-landing { font-family: 'DM Sans', Inter, system-ui, -apple-system, sans-serif; background: #05070d; color: #ffffff; min-height: 100vh; }
        .dxr-landing a { color: inherit; }
        .dxr-landing img { max-width: 100%; }

        /* ---- Scroll experience: smooth + gentle section snapping ----
           These html/#top rules are mounted only while the landing route is
           rendered (this <style> unmounts on navigation), so they don't affect
           the rest of the app. 'proximity' (not 'mandatory') so sections taller
           than the viewport never trap the scroll below 1024px. */
        html { scroll-behavior: smooth; scroll-snap-type: y proximity; scroll-padding-top: 80px; }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; scroll-snap-type: none; }
        }

        /* Uniform vertical rhythm: equal spacing between consecutive sections. */
        :root { --section-space: clamp(48px, 5.5vw, 84px); }
        #top > section, #top > footer { scroll-snap-align: start; scroll-margin-top: 80px; }
        #top > section { padding-top: var(--section-space); padding-bottom: var(--section-space); }
        #top > section:first-of-type { padding-top: clamp(16px, 3vw, 36px); }

        /* ---- Full-page mode (desktop): each scroll brings the next section to
           fill the screen. JS (FullpageScroll) drives one-gesture-per-section
           jumps, so CSS snap is off here to avoid fighting the scripted smooth
           scroll. Content is biased slightly above geometric center so the tall
           cards don't pull the eye down (optical centering). */
        @media (min-width: 1024px) {
          html { scroll-snap-type: none; }
          #top > section {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding-top: clamp(32px, 4vh, 52px);
            padding-bottom: clamp(96px, 13vh, 160px);
            scroll-margin-top: 0;
          }
          /* Hero is the tallest section (h1 + sub + card + steps); top-align it
             and keep its bottom padding tight so the steps strip has clearance
             instead of being clipped. */
          #top > section.dxr-hero {
            justify-content: flex-start;
            padding-top: clamp(56px, 6vh, 84px);
            padding-bottom: clamp(12px, 2vh, 28px);
          }
        }
      `}</style>
      <main id="top">
        <FullpageScroll />
        <SiteNav />

        <DxrHeroCarousel />

        <DxrOnboarding />

        <DxrDictateLess />

        <DxrCapabilities />

        <DxrResults />

        <FaqAccordion faqs={dxrFaqs} />

        {/* Final CTA band */}
        <section
          style={{
            margin: "clamp(48px, 5.5vw, 84px) 5%",
            padding: "clamp(40px, 6vw, 72px) 6%",
            borderRadius: "24px",
            background:
              "radial-gradient(120% 140% at 50% 0%, rgba(41,151,255,0.18) 0%, rgba(41,151,255,0.04) 45%, rgba(255,255,255,0.03) 100%)",
            border: "1px solid rgba(41,151,255,0.25)",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "clamp(28px, 4.2vw, 48px)",
              fontWeight: 700,
              color: "#fff",
              letterSpacing: "-0.02em",
              margin: "0 0 14px",
            }}
          >
            Stop typing your reports.
          </h2>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "clamp(15px, 1.8vw, 19px)",
              lineHeight: "160%",
              color: "rgba(255,255,255,0.75)",
              maxWidth: "600px",
              margin: "0 auto 32px",
            }}
          >
            Dictate your next case and watch DXR build a structured, sign-off-ready report in seconds. Start free — no card required.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "center" }}>
            <a href={TRY_URL} style={{ ...btn, color: "#000", background: "#2997FF" }}>
              Try now →
            </a>
            <BookDemoButton style={{ ...btn, color: "#fff", background: "transparent", border: "1px solid rgba(255,255,255,0.3)" }} />
          </div>
        </section>

        <SiteFooter />
      </main>
    </div>
  );
}
